import React from 'react';

import {useSelector} from '@xstate/react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa/qa';
import {SubTitleMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {WeekdayPicker} from '@spaced-out/ui-design-system/lib/components/WeekdayPicker';
import type {Weekday} from '@spaced-out/ui-design-system/lib/components/WeekdayPicker';

import {
  selectCanNavigateNext,
  selectCanNavigatePrevious,
  selectWeekRangeDisplay,
  selectWeekDays,
  selectSelectedDate,
  selectCurrentSequenceNumber,
  selectEventTemplate,
} from '../../selectors/booking-selector';
import {useBookingMachine} from '../../hooks/useBookingMachine';
import {useIsMobile} from '../../hooks/useIsMobile';
import {TEST_ID} from '../../utils/testId';

import css from './booking-week-day-picker.css';


export const BookingWeekDayPicker = () => {
  const {t, language} = useLabelTranslation();
  const isMobile = useIsMobile();
  const {bookingActor} = useBookingMachine();
  const canNavigateNext = useSelector(bookingActor, selectCanNavigateNext);
  const canNavigatePrevious = useSelector(
    bookingActor,
    selectCanNavigatePrevious,
  );
  const selectedDayForPicker = useSelector(bookingActor, (state) =>
    selectSelectedDate(state, language),
  );

  const weekRangeDisplay = useSelector(bookingActor, (state) =>
    selectWeekRangeDisplay(state, language),
  );
  const {weekDayOptions, disabledWeekDays} = useSelector(
    bookingActor,
    (state) => selectWeekDays(state, language),
  );

  const handleSelectDay = (weekDay: Weekday[]) => {
    if (weekDay.length === 0) {
      return;
    }
    bookingActor.send({type: 'date.change', date: weekDay[0].key});
  };

  const handleNavigatePrevious = () => {
    bookingActor.send({type: 'week.prev'});
  };

  const handleNavigateNext = () => {
    bookingActor.send({type: 'week.next'});
  };

  const currentSequenceNumber = useSelector(bookingActor, (state) =>
    selectCurrentSequenceNumber(state),
  );

  const eventTemplate = useSelector(bookingActor, selectEventTemplate);
  const isMultiDay =
    eventTemplate?.metadata?.sequence?.length &&
    eventTemplate.metadata.sequence.length > 1;
  const isSequentialMultiDay =
    eventTemplate?.distribution_rule === 'sequential_interview' && isMultiDay;

  return (
    <div className={css.weekNavigationContainer}>
      {isSequentialMultiDay && (
        <SubTitleMedium className={css.currentSequenceNumber}>
          {`${t('Day', 'Day')} ${currentSequenceNumber + 1}`}
        </SubTitleMedium>
      )}
      <div className={css.weekNavigation}>
        <Button
          type="ghost"
          iconLeftName="angle-left"
          onClick={handleNavigatePrevious}
          disabled={!canNavigatePrevious}
          aria-label="Navigate to previous week"
          testId={generateTestId({
            base: TEST_ID.BOOKING_FLOW,
            slot: 'previous-week-button',
          })}
        />
        <SubTitleMedium
          testId={generateTestId({
            base: TEST_ID.BOOKING_FLOW,
            slot: 'week-range-display',
          })}
        >
          {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
          {weekRangeDisplay}
        </SubTitleMedium>
        <Button
          type="ghost"
          iconLeftName="angle-right"
          onClick={handleNavigateNext}
          disabled={!canNavigateNext}
          aria-label="Navigate to next week"
          testId={generateTestId({
            base: TEST_ID.BOOKING_FLOW,
            slot: 'next-week-button',
          })}
        />
      </div>
      <WeekdayPicker
        key={selectedDayForPicker?.key}
        size={isMobile ? 'small' : 'medium'}
        disableMultiSelect={true}
        customDayLabels={weekDayOptions}
        disabledWeekDays={disabledWeekDays}
        onChange={handleSelectDay}
        classNames={{
          wrapper: css.weekDayPickerWrapper,
          selectedButton: css.weekDayPickerSelectedButton,
        }}
        selectedWeekDays={selectedDayForPicker ? [selectedDayForPicker] : []}
        testId={generateTestId({
          base: TEST_ID.BOOKING_FLOW,
          slot: 'weekday-picker',
        })}
      />
    </div>
  );
};
